export async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Crops image with rotation & returns a Blob.
 * croppedAreaPixels: { x, y, width, height }
 */
/**
 * @param {string} imageSrc
 * @param {{x:number,y:number,width:number,height:number}} croppedAreaPixels
 * @param {number} rotation
 * @param {string} outMime
 * @param {{ mask?: "none" | "circle", maxSize?: number }} [options]
 */
export async function getCroppedBlob(imageSrc, croppedAreaPixels, rotation = 0, outMime = "image/png", options) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas không hỗ trợ");

  const opts = options && typeof options === "object" ? options : {};
  const mask = opts.mask || "none";
  const maxSize = typeof opts.maxSize === "number" && opts.maxSize > 0 ? opts.maxSize : null;

  const rotRad = getRadianAngle(rotation);

  const maxImgSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxImgSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(rotRad);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(image, safeArea / 2 - image.width / 2, safeArea / 2 - image.height / 2);

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - croppedAreaPixels.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - croppedAreaPixels.y)
  );

  // Resize (best-effort) for smaller uploads
  let outCanvas = canvas;
  if (maxSize && (canvas.width > maxSize || canvas.height > maxSize)) {
    const scale = Math.min(maxSize / canvas.width, maxSize / canvas.height);
    const w = Math.max(1, Math.round(canvas.width * scale));
    const h = Math.max(1, Math.round(canvas.height * scale));
    const resized = document.createElement("canvas");
    resized.width = w;
    resized.height = h;
    const rctx = resized.getContext("2d");
    if (!rctx) throw new Error("Canvas không hỗ trợ");
    rctx.imageSmoothingEnabled = true;
    rctx.imageSmoothingQuality = "high";
    rctx.drawImage(canvas, 0, 0, w, h);
    outCanvas = resized;
  }

  // Apply circle mask (transparent corners) for round avatars
  if (mask === "circle") {
    const masked = document.createElement("canvas");
    masked.width = outCanvas.width;
    masked.height = outCanvas.height;
    const mctx = masked.getContext("2d");
    if (!mctx) throw new Error("Canvas không hỗ trợ");
    const r = Math.min(masked.width, masked.height) / 2;
    mctx.clearRect(0, 0, masked.width, masked.height);
    mctx.save();
    mctx.beginPath();
    mctx.arc(masked.width / 2, masked.height / 2, r, 0, Math.PI * 2);
    mctx.closePath();
    mctx.clip();
    mctx.drawImage(outCanvas, 0, 0);
    mctx.restore();
    outCanvas = masked;
  }

  return new Promise((resolve, reject) => {
    outCanvas.toBlob((blob) => {
      if (!blob) reject(new Error("Không tạo được ảnh sau crop"));
      else resolve(blob);
    }, outMime);
  });
}

